"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizeDesignConfigOnPersist = exports.sanitizeWaypointDocument = exports.sanitizeEmailUrl = exports.sanitizeCustomHtmlWrappedContents = exports.sanitizeEmailHtml = exports.CUSTOM_HTML_WRAPPER_CLOSE = exports.CUSTOM_HTML_WRAPPER_OPEN = void 0;
const sanitize_html_1 = __importDefault(require("sanitize-html"));
const common_1 = require("../enums/common");
exports.CUSTOM_HTML_WRAPPER_OPEN = '<div data-block-type="custom-html">';
exports.CUSTOM_HTML_WRAPPER_CLOSE = "</div>";
const sanitizeEmailHtml = (htmlBodyString, { allowDataImages = true } = {}) => {
    return (0, sanitize_html_1.default)(htmlBodyString, {
        allowedTags: [
            ...sanitize_html_1.default.defaults.allowedTags,
            "p",
            "br",
            "b",
            "i",
            "em",
            "strong",
            "a",
            "img",
            "ul",
            "ol",
            "li",
            "table",
            "tr",
            "td",
            "th",
            "thead",
            "tbody",
            "span",
        ],
        allowedAttributes: {
            "*": ["style"],
            span: ["contenteditable", "data-custom-variable", "fr-original-class", "fr-original-style"],
            a: [
                "href",
                "target",
                "name",
                "rel",
                "data-social-platform",
                "data-icon-color",
                "data-icon-background-color",
                "data-video-link",
            ],
            div: ["data-video-thumbnail", "data-video-thumbnail-wrapper"],
            img: ["src", "srcset", "alt", "width", "height", "align", "data-video-thumbnail-img", "data-video-play-icon"],
            table: [
                "width",
                "height",
                "cellpadding",
                "cellspacing",
                "border",
                "role",
                "align",
                "bgcolor",
                "data-video-thumbnail-table",
            ],
            td: ["align", "valign", "width", "height", "bgcolor"],
            th: ["align", "valign", "width", "height", "bgcolor"],
        },
        // Variable chips (firstname/lastname/last_amount) style themselves via the `styled-tag-chip` class.
        allowedClasses: {
            span: ["styled-tag-chip", "fr-deletable"],
        },
        allowedSchemes: ["http", "https", "mailto"],
        allowedSchemesByTag: {
            img: allowDataImages ? ["http", "https", "data"] : ["http", "https"],
        },
        transformTags: {
            "*": (tagName, attribs) => {
                delete attribs.onclick;
                delete attribs.onmouseover;
                delete attribs.onload;
                delete attribs.onerror;
                delete attribs.id;
                // Preserve Kreola's image styling safely
                if (tagName === "img" && attribs.style && !attribs.align) {
                    if (/float\s*:\s*left/i.test(attribs.style)) {
                        attribs.align = "left";
                    }
                    else if (/float\s*:\s*right/i.test(attribs.style)) {
                        attribs.align = "right";
                    }
                }
                if (attribs.style) {
                    const hasWidth = attribs.style.includes("width:");
                    const allowedStyles = attribs.style
                        .split(";")
                        .filter((style) => {
                        const property = style.split(":")[0]?.trim().toLowerCase() ?? "";
                        return (!/^(position|z-index|top|left|right|bottom|float|clear|visibility)$/.test(property) &&
                            !/(expression|behavior|javascript|eval)/i.test(style));
                    })
                        .concat(hasWidth ? ["max-width: 100%"] : [])
                        .join(";");
                    if (allowedStyles) {
                        attribs.style = allowedStyles;
                    }
                    else {
                        delete attribs.style;
                    }
                }
                return { tagName, attribs };
            },
        },
    });
};
exports.sanitizeEmailHtml = sanitizeEmailHtml;
const BLOCK_TYPE_WRAPPER = /^(<div\b[^>]*\bdata-block-type="(?:custom-html|socials|video)"[^>]*>)([\s\S]*)(<\/div>)\s*$/i;
const EMAIL_CONTENT_WRAPPER = /^(<div\b[^>]*\bid="email-content-wrapper"[^>]*>(?:<style\b[^>]*>[\s\S]*?<\/style>)?)([\s\S]*)(<\/div>)\s*$/i;
const sanitizeCustomHtmlWrappedContents = (contents, options = {}) => {
    if (!contents) {
        return contents;
    }
    const blockTypeMatch = contents.match(BLOCK_TYPE_WRAPPER);
    if (blockTypeMatch) {
        const [, opening, inner, closing] = blockTypeMatch;
        return `${opening}${(0, exports.sanitizeEmailHtml)(inner, options)}${closing}`;
    }
    const emailWrapperMatch = contents.match(EMAIL_CONTENT_WRAPPER);
    if (emailWrapperMatch) {
        const [, opening, inner, closing] = emailWrapperMatch;
        return `${opening}${(0, exports.sanitizeEmailHtml)(inner, options)}${closing}`;
    }
    return (0, exports.sanitizeEmailHtml)(contents, options);
};
exports.sanitizeCustomHtmlWrappedContents = sanitizeCustomHtmlWrappedContents;
const sanitizeEmailUrl = (value, options = {}) => {
    if (typeof value !== "string") {
        return "";
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return "";
    }
    let parsed;
    try {
        parsed = new URL(trimmed);
    }
    catch {
        return "";
    }
    const allowed = options.allowDataScheme ? ["http:", "https:", "mailto:", "data:"] : ["http:", "https:", "mailto:"];
    return allowed.includes(parsed.protocol) ? trimmed : "";
};
exports.sanitizeEmailUrl = sanitizeEmailUrl;
const URL_FIELDS_BY_BLOCK_TYPE = {
    Button: [{ field: "url" }],
    Image: [{ field: "url", allowDataScheme: true }, { field: "linkHref" }],
};
const sanitizeWaypointDocument = (document, options = {}) => {
    const { allowDataImages = true } = options;
    const next = { ...document };
    for (const blockId of Object.keys(next)) {
        const block = next[blockId];
        if (!block) {
            continue;
        }
        if (block.type === "Html") {
            const contents = block.data.props?.contents;
            if (typeof contents !== "string" || contents === "") {
                continue;
            }
            const sanitized = (0, exports.sanitizeCustomHtmlWrappedContents)(contents, options);
            if (sanitized !== contents) {
                next[blockId] = {
                    ...block,
                    data: {
                        ...block.data,
                        props: { ...block.data.props, contents: sanitized },
                    },
                };
            }
            continue;
        }
        const rules = URL_FIELDS_BY_BLOCK_TYPE[block.type];
        if (!rules) {
            continue;
        }
        const data = block.data;
        const props = data.props;
        if (!props) {
            continue;
        }
        let mutated = false;
        const nextProps = { ...props };
        for (const rule of rules) {
            const value = props[rule.field];
            if (typeof value !== "string") {
                continue;
            }
            const sanitized = (0, exports.sanitizeEmailUrl)(value, { allowDataScheme: rule.allowDataScheme && allowDataImages });
            if (sanitized !== value) {
                nextProps[rule.field] = sanitized;
                mutated = true;
            }
        }
        if (mutated) {
            next[blockId] = {
                ...block,
                data: { ...block.data, props: nextProps },
            };
        }
    }
    return next;
};
exports.sanitizeWaypointDocument = sanitizeWaypointDocument;
const sanitizeDesignConfigOnPersist = (designConfig, editorVersion) => {
    if (designConfig === null || designConfig === undefined || editorVersion !== common_1.EmailEditorVersion.Waypoint) {
        return { sanitized: designConfig, didSanitize: false };
    }
    const sanitized = (0, exports.sanitizeWaypointDocument)(designConfig);
    const didSanitize = JSON.stringify(sanitized) !== JSON.stringify(designConfig);
    return { sanitized, didSanitize };
};
exports.sanitizeDesignConfigOnPersist = sanitizeDesignConfigOnPersist;
